import * as types from '../actiontypes';
import env from '../../util/env';
import {serviceURL} from '../../util/uiconstants';
import Utility from '../../util/utilityfunctions';
import {
	API_RESOURCES,
	createApiUpdateAction,
	HTTP_REQUEST_METHODS,
	createApiReadAction,
	createApiCreateAction,
	createApiDeleteAction,
	createApiPatchAction
} from '../../util/api';
import {promiseAreas} from '../../util/uiconstants';

/**
 * Fetch all accounts for an engagement
 * @param {Number | String} engagementId Engagement Id.
 * @param {Object} queryString Query parameters for filtering or sorting.
 * @param {String} trackWithArea Area to track the API call with.
 * @returns {Object} Redux action to fetch accounts.
 */
export const getAccounts = (engagementId, queryString, trackWithArea = null) =>
	createApiReadAction(
		types.GET_ACCOUNTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: null
		},
		queryString,
		{resolve: true},
		trackWithArea
	);

/**
 * List accounts for an engagement using POST request body.
 * Use this endpoint when the number or size of query‑string parameters may exceed standard URL length limits.
 * @param {Number | String} engagementId Engagement Id.
 * @param {Object} listParams List parameters in request body.
 * @param {String} trackWithArea Area to track the API call with.
 * @returns {Object} Redux action to list accounts.
 */
export const listAccounts = (actionType, engagementId, listParams, trackWithArea = null) => {
	// documentId is passed as query parameter to allow listaccounts POST call to pass for CEAP users
	const {documentId, ...bodyParams} = listParams || {};
	const baseUrl = `${env.getBaseURL(serviceURL, engagementId)}listaccounts`;

	const searchParams = new URLSearchParams();
	if (documentId) {
		searchParams.set('documentId', documentId.toString());
	}
	const queryString = searchParams.toString();
	const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

	return {
		type: actionType,
		resolve: true,
		meta: {
			api: {
				url,
				method: 'POST',
				data: bodyParams,
				trackWithArea
			}
		}
	};
};

export const getAccountsForEstimate = (engagementId, queryString, mergeStrategy = null, handleCustomState = null) =>
	createApiReadAction(
		types.GET_ACCOUNTS_FOR_ESTIMATE,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: null
		},
		queryString,
		{resolve: true, mergeStrategy: mergeStrategy, handleCustomState: handleCustomState}
	);

/**
 * Fetch account metrics for an engagement
 * @param {String | Number} engagementId Engagement Id.
 * @param {Object} queryString Query parameters for filtering or sorting.
 * @param {Object} actionProps Additional properties for the action.
 * @returns {Object} Redux action to fetch account metrics.
 */
export function getAccountMetrics(engagementId, queryString, actionProps) {
	return createApiReadAction(
		types.GET_ACCOUNT_METRICS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTMETRICS]: null
		},
		queryString,
		// Need the query string parameters in the action payload
		{
			...queryString,
			...actionProps
		}
	);
}

export const getAccountByIdDefaultQueryParams = {
	includeAssertions: true,
	includePspIndexes: true,
	includeRelatedRisks: true,
	includeHelix: false,
	includeEntities: true,
	includeRelatedDocuments: true,
	includeAttributes: true,
	includeControlV2: true,
	includeRiskResponsiveTasks: true
};

export const getAccountById = (engagementId, accountId, queryParameters, actionProps) => {
	const url =
		env.getBaseURL(serviceURL, engagementId) + `Accounts/${accountId}?` + Utility.getAccountParams(queryParameters || getAccountByIdDefaultQueryParams);

	return {
		type: types.GET_ACCOUNT_BY_ID,
		...actionProps,
		meta: {
			api: {
				url,
				method: 'GET',
				...(actionProps?.track && {trackWithArea: promiseAreas.GET_ACCOUNT})
			}
		},
		resolve: true
	};
};

export const updateAccount = (engagementId, accountId, data, actionProps) => {
	const action = createApiUpdateAction(
		types.UPDATE_ACCOUNT,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: accountId
		},
		data,
		HTTP_REQUEST_METHODS.PUT,
		actionProps
	);
	action.resolve = true;
	return action;
};

export const createAccount = (engagementId, data, actionProps) => {
	const url = `${env.getBaseURL(serviceURL, engagementId)}Accounts`;
	return {
		type: types.CREATE_ACCOUNT,
		...actionProps,
		meta: {
			api: {
				url,
				method: 'POST',
				data
			}
		}
	};
};

export function cleanAccount() {
	return {
		type: types.CLEAN_ACCOUNT
	};
}

export function deleteAccount(engagementId, accountId) {
	let url = env.getBaseURL(serviceURL, Number(engagementId)) + 'Accounts' + '/' + accountId;

	return {
		type: types.DELETE_ACCOUNT,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			}
		},
		accountId
	};
}

export function putAssociatedHelixAccounts(engagementId, accountList) {
	const url = `${env.getBaseURL(serviceURL, engagementId)}HelixAccountMappings`;

	return {
		type: types.SAVE_BATCH_HELIX_CANVAS_ACCOUNT_RELATIONS,
		meta: {
			api: {
				url: url,
				method: 'PUT',
				data: accountList
			}
		}
	};
}

export function getAllEngagementAccounts(engagementId, actionProps, queryParameters) {
	const defaultQueryString = {
		orderBy: 'AccountName',
		IncludeAssertions: true,
		IncludePspIndexes: true,
		IncludeRelatedRisks: true,
		IncludeHelix: false,
		IncludeEntities: true,
		IncludeRelatedDocuments: true,
		includeControlV2: true
	};

	return createApiReadAction(
		types.GET_ACCOUNTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: null
		},
		queryParameters || defaultQueryString,
		{...actionProps, resolve: true}
	);
}

export function areAllCanvasAccountsMappedToHelixAccounts(engagementId) {
	const url = `${env.getBaseURL(serviceURL, engagementId)}HelixAccountMappings`;

	return {
		type: types.GET_IS_HELIX_CANVAS_ACCOUNT_MAPPINGS_SUCCESSFULL,
		meta: {
			api: {
				url: url,
				method: 'GET'
			}
		}
	};
}

export function saveSentAssociatedHelixAccounts(accountList) {
	return {
		type: types.SAVE_SENT_ASSOCIATED_HELIX_ACCOUNTS,
		accountList
	};
}

export function relateEntityAccount(engagementId, accountId, stEntityId) {
	return createApiCreateAction(
		types.RELATE_ENTITY_ACCOUNT,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.MULTIENTITY]: stEntityId,
			[API_RESOURCES.ACCOUNT]: undefined
		},
		{accountId},
		{accountId, stEntityId}
	);
}

export function unrelateEntityAccount(engagementId, accountId, stEntityId) {
	return createApiDeleteAction(
		types.UNRELATE_ENTITY_ACCOUNT,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.MULTIENTITY]: stEntityId,
			[API_RESOURCES.ACCOUNT]: accountId
		},
		{accountId, stEntityId}
	);
}

export function updateStoreAccountRelation(data) {
	return {
		type: types.UPDATE_STORE_ACCOUNT_ASSERTION,
		payload: data
	};
}

export function relateAccountToScot(engagementId, accountId, payload, customErrorMessage = undefined) {
	return createApiCreateAction(
		types.RELATE_ACCOUNT_TO_SCOT,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: accountId,
			[API_RESOURCES.SCOT]: undefined
		},
		payload,
		{customErrorMessage}
	);
}

export function unrelateAccountToScot(engagementId, accountId, scotId, customErrorMessage) {
	return createApiDeleteAction(
		types.UNRELATE_ACCOUNT_TO_SCOT,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: accountId,
			[API_RESOURCES.SCOT]: scotId
		},
		{customErrorMessage}
	);
}

export function getAllCRAAccounts(engagementId, queryString) {
	return createApiReadAction(
		types.GET_ALL_CRA_ACCOUNTS,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: null
		},
		queryString,
		{resolve: true}
	);
}

export const patchAccountRationale = (engagementId, accountId, data, actionProps) => {
	const action = createApiPatchAction(
		types.PATCH_ACCOUNT_RATIONALE,
		{
			[API_RESOURCES.ENGAGEMENTS]: engagementId,
			[API_RESOURCES.ACCOUNTS]: accountId
		},
		[
			{
				op: 'replace',
				path: '/Rationale',
				value: data
			}
		],
		actionProps
	);
	action.resolve = true;
	return action;
};

export const appendScotToAccount = (engagementId, accountId, data, actionProps) => {
	// Instead of making a server call, dispatch a local action to update the store
	const action = {
		type: types.APPEND_SCOT_TO_ACCOUNT,
		payload: {
			engagementId,
			accountId,
			data
		},
		...actionProps
	};
	action.resolve = true;
	return action;
};

export const deleteScotFromAccount = (engagementId, accountId, data, actionProps) => {
	// Instead of making a server call, dispatch a local action to update the store
	const action = {
		type: types.DELETE_SCOT_FROM_ACCOUNT,
		payload: {
			engagementId,
			accountId,
			data
		},
		...actionProps
	};
	action.resolve = true;
	return action;
};

/**
 * Fetch only trial balance mappings for an account.
 * Used to resolve trialBalanceId before fetching leadsheet data.
 * @param {Number | String} engagementId Engagement Id.
 * @param {Number | String} accountId Account Id.
 * @returns {Object} Redux action to fetch account trial balance mappings.
 */
export const getAccountTrialBalanceById = (engagementId, accountId) => {
	const url = env.getBaseURL(serviceURL, engagementId) + `Accounts/${accountId}?` + Utility.getAccountParams({includeTrialBalance: true});

	return {
		type: types.GET_ACCOUNT_TRIAL_BALANCE_BY_ID,
		accountId,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		},
		resolve: true
	};
};
